import {
  fetchUrlStyleName,
  softGateStyle,
} from '@chegg-wt/core/state/currentProject/actions'
import { AppState } from '@chegg-wt/core/state'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Advert } from '@chegg-wt/core/components/Advert'
import BackLink from '@chegg-wt/core/components/BackLink'
import FormTitle from '@chegg-wt/core/components/Form/FormTitle'
import CMSContent from '@chegg-wt/core/components/SEO/FormB1'
import ErrorContainer from '@chegg-wt/core/components/ErrorContainer'
import {
  formLoaded,
  writingFormSubmit,
  formSubmitStarted,
} from '@chegg-wt/core/state/form/actions'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import {
  createCitationSourceTypeSelector,
  createCitationQuerySelector,
  adUnlockVariationSelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import {
  currentStyleSelector,
  currentProjectIdSelector,
} from '@chegg-wt/core/state/currentProject/selectors'
import WritingForms, {
  SourceType as FormSourceType,
  IntialCitationData,
  FormMode,
  PubType,
} from '@chegg/writing-forms'
import { shouldBlockOnCitationCountSelector } from '@chegg-wt/core/state/bibliography/selectors'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import {
  GATED_BIBLIOGRAPHY_ON_CITATION_COUNT_MODAL,
  REDESIGNED_UNLOCK_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { FormType, FormTypes } from '@chegg-wt/core/constants/form'
import { getCookie, Cookies } from '@chegg-wt/core/utils/cookies'
import { Config } from '@chegg-wt/core/config'
import { isProSelector, userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { GatedBibliographyOnCitationCountModalData } from '@chegg-wt/core/state/bibliography/types'

import { activeCitationSelector } from '@chegg-wt/core/state/citation/selectors'
import { evalSourceTypes } from '@chegg-wt/core/utils/searchSources'
import {
  getSessionStorageItem,
  SESSION_STORAGE_KEYS,
  setSessionStorageItem,
} from '@chegg-wt/core/utils/sessionStorage'
import { sourceTypeSelected } from '@chegg-wt/core/state/createCitation/actions'
import { sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import { getCitationTitle, replaceCitationTitle } from '@chegg-wt/core/utils/citations'
import { isStyleGated } from '@chegg-wt/core/utils/styles'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import { AuthRedirectActions } from '@chegg-wt/core/state/user/types'
import { setUpgradeCitationInSessionStorage } from '@chegg-wt/core/utils/upgrade'
import { bff } from '@chegg-wt/core/api/bff'
import { UserRoles } from '@chegg-wt/core/constants/user'
import cloneDeep from 'lodash.clonedeep'
import { AdUnlockVariations } from '@chegg-wt/core/types/core'
import { useRouter } from 'next/router'
import { LvpMarginWrapper } from '../LargeVideoPlayerLayout'
import {
  backButtonConfigs,
  getFormMode,
  citationToFormStateTransformation,
  formStateToCitationTransformation,
  getFormsBackButtonText,
} from './utils'

import { FormPageProps, FormPageStateProps, FormPageDispatchProps } from './types'
import * as Styled from './styled'
import { WritingFormsWrapper } from './styled'

export const noop = (): void => undefined

export const noopAsync = async (): Promise<void> => undefined

export const FormPageTop: React.FC = () => (
  <>
    <BackLink />
    <ErrorContainer />
    <FormTitle />
  </>
)

export const shouldSubmitForm = (
  formType: FormType,
  isProUser: boolean,
  showAdUnlockFirstVariant: boolean,
  showAdUnlockSecondVariant: boolean
) => {
  const hasRecentlyViewedUnlock =
    getCookie(Cookies.PreviousTruex) === 'true' ||
    getCookie(Cookies.ViewedAdUnlock) === 'true'
  const isUnlockReady = window?.SBMGlobal?.run?.showUnlock
  const isEditing = formType === FormTypes.EDIT

  const shouldSubmit =
    isProUser ||
    hasRecentlyViewedUnlock ||
    (!isUnlockReady && !showAdUnlockFirstVariant && !showAdUnlockSecondVariant) ||
    (isEditing && !Config.editCitationGate)

  return shouldSubmit
}

export const FormPage: React.FC<FormPageProps> = ({
  urlStyleNameFetched,
  copy,
  currentStyle,
  currentUrlStyle,
  sourceType,
  formType,
  projectId,
  searchQuery,
  formLoaded,
  writingFormSubmit,
  formSubmitStarted,
  isProUser,
  shouldBlockCitationCreation = false,
  navigated,
  openModal,
  activeCitation,
  selectSourceType,
  softGateStyle,
  userRole,
  adUnlockModalExpVariant,
}) => {
  const [currentCitation, setCurrentCitation] = useState<Citation>(null)
  const formMode = getFormMode(formType)

  const showAdUnlockFirstVariant =
    adUnlockModalExpVariant === AdUnlockVariations.VARIANT1 &&
    userRole === UserRoles.NOT_LOGGED_IN
  const showAdUnlockSecondVariant =
    adUnlockModalExpVariant === AdUnlockVariations.VARIANT2 &&
    userRole === UserRoles.NOT_LOGGED_IN

  const [initialCitationData, setInitialCitationData] = useState<IntialCitationData>({
    data: {
      source:
        sourceType &&
        (sourceTypeById(sourceType.toLowerCase()).apiAlias as FormSourceType),
    },
    annotation: '',
  })
  const backButtonConfig = backButtonConfigs[formType]
  const {
    [backButtonConfig.route]: toBackRoute,
    toSourceSelection,
    toManualCitation,
  } = useRoutesContext()

  const capitalizeTitle = async () => {
    let title = getCitationTitle(activeCitation)
    if (title && formType === FormTypes.NEW) {
      ;[title] = await bff.capitalizeCitations({
        citationsInput: [title],
      })
    }
    setCurrentCitation(replaceCitationTitle(cloneDeep(activeCitation), title))
  }

  const hasBreadcrumbs = typeof currentUrlStyle === 'string'

  useEffect(() => {
    formLoaded(currentStyle, sourceType)
    capitalizeTitle()
  }, [activeCitation])

  useEffect(() => {
    if (currentCitation && formType !== FormTypes.MANUAL) {
      setInitialCitationData(formStateToCitationTransformation(currentCitation))
    } else {
      const updateInitialCitationData = { ...initialCitationData }
      updateInitialCitationData.data.source =
        sourceType &&
        (sourceTypeById(sourceType.toLowerCase()).apiAlias as FormSourceType)
      setInitialCitationData(updateInitialCitationData)
    }
  }, [formMode, sourceType, currentCitation])

  useEffect(() => {
    if (hasBreadcrumbs) {
      urlStyleNameFetched(currentUrlStyle)
    }
  }, [currentUrlStyle])

  const { bibliographyCitationCountThreshold: blockThreshold } = Config

  /* Use this handler to show the redesigned video-primary UCF unlock modal */
  const redesignedGateHandler = ({
    withAuthBanner = true,
    alwaysParticipate = false,
  }) => (event) => {
    const citation = citationToFormStateTransformation(event)
    if (
      shouldSubmitForm(
        formType,
        isProUser,
        showAdUnlockFirstVariant,
        showAdUnlockSecondVariant
      )
    ) {
      formSubmitStarted()
      return writingFormSubmit({ citation, initialCitationData })
    }
    setSessionStorageItem(
      SESSION_STORAGE_KEYS.citationObject,
      JSON.stringify({
        citation,
        initialCitationData,
        route: {
          action: AuthRedirectActions.CREATE_CITATION,
          projectId,
          style: currentUrlStyle || currentStyle,
          isPaymentDone: false,
        },
      })
    )
    openModal({
      name: REDESIGNED_UNLOCK_MODAL,
      data: { withAuthBanner, alwaysParticipate },
    })
  }

  const citationCountGatedHandler = () => {
    openModal({
      name: GATED_BIBLIOGRAPHY_ON_CITATION_COUNT_MODAL,
      data: {
        blockThreshold,
        isWarningOnly: !shouldBlockCitationCreation,
        currentStyle,
      } as GatedBibliographyOnCitationCountModalData,
    })
  }

  const mainHandleSubmit = (event) => {
    const isProjectStyleGated = isStyleGated(currentStyle, userRole)
    const isUrlStyleAllowed = currentUrlStyle
      ? !isStyleGated(currentUrlStyle, userRole)
      : null
    const isGuestGatedToAllowedStyle =
      userRole === UserRoles.NOT_LOGGED_IN && isProjectStyleGated && isUrlStyleAllowed
    const isFreeGatedToAllowedStyle =
      userRole === UserRoles.FREE_USER && isProjectStyleGated && isUrlStyleAllowed
    if (
      Config.enableStyleGate &&
      (isGuestGatedToAllowedStyle ||
        isFreeGatedToAllowedStyle ||
        (isUrlStyleAllowed === null ? isProjectStyleGated : !isUrlStyleAllowed))
    ) {
      if (!isGuestGatedToAllowedStyle) {
        // set citations to upgrade session item in all modals except ChangeToFreeStyleModal
        const citation = citationToFormStateTransformation(event)
        setUpgradeCitationInSessionStorage({
          citation,
          initialCitationData,
          route: {
            action: AuthRedirectActions.CREATE_CITATION,
            projectId,
            style: currentUrlStyle || currentStyle,
            isPaymentDone: false,
          },
        })
        if (citation) {
          setSessionStorageItem(
            SESSION_STORAGE_KEYS.citationObject,
            JSON.stringify({
              citation,
              initialCitationData,
              route: {
                action: AuthRedirectActions.CREATE_CITATION,
                projectId,
                style: currentUrlStyle || currentStyle,
                isPaymentDone: false,
              },
            })
          )
        }
      }

      return softGateStyle(currentUrlStyle, true, () => redesignedGateHandler({})(event))
    }

    if (shouldBlockCitationCreation) return citationCountGatedHandler()
    return redesignedGateHandler({})(event)
  }

  const backButtonclickHandler = () => {
    const skipEvalPage = !evalSourceTypes.includes(sourceType)

    const route =
      skipEvalPage && formType === FormTypes.NEW
        ? toSourceSelection({
            sourceType,
            style: currentStyle,
            query: encodeURIComponent(searchQuery),
          })
        : toBackRoute({ sourceType, style: currentStyle, projectId })

    navigated(route)
  }

  const onFormSourceUpdate = (source: string, pubTypeData?: PubType): void => {
    selectSourceType(source) // update form title
    const route = toManualCitation({ sourceType: source, style: currentStyle })
    navigated(route) // navigate to specific form
    const initialCitationDataForSourceUpdate: IntialCitationData = JSON.parse(
      JSON.stringify(initialCitationData)
    ) // creating a deep copy
    initialCitationDataForSourceUpdate.data.source = source.toLowerCase() as FormSourceType
    if (pubTypeData) {
      initialCitationDataForSourceUpdate.data.pubtype = pubTypeData
    }
    if (
      formMode &&
      formMode === FormMode.EDIT &&
      initialCitationData.data?.source === FormSourceType.BOOK &&
      initialCitationData.data.pubtype
    ) {
      initialCitationDataForSourceUpdate.data.pubtype = {
        ...initialCitationData.data.pubtype,
      }
    }
    setInitialCitationData(initialCitationDataForSourceUpdate)
  }

  const router = useRouter()
  const { authSuccess } = router.query as { authSuccess: string }
  useEffect(() => {
    if (authSuccess) {
      if (
        JSON.parse(getSessionStorageItem(SESSION_STORAGE_KEYS.citationObject))?.citation
      ) {
        try {
          const storedCitation = JSON.parse(
            getSessionStorageItem(SESSION_STORAGE_KEYS.citationObject)
          ).citation
          const obj = {
            data: {
              ...storedCitation,
            },
          }
          setInitialCitationData(obj)
        } catch (error) {
          console.error('Error parsing stored citation:', error)
        }
      }
    } // Runs when authComplete is detected
  }, [authSuccess])

  return (
    <Styled.Wrapper>
      <div className="container-fluid form-page">
        <LvpMarginWrapper isFullWidth>
          <Styled.FormMainWrapper>
            <WritingFormsWrapper className="form-package-wrapper">
              {/* writingForms package integration */}
              <WritingForms
                mode={formMode}
                intialCitationState={initialCitationData}
                onSave={mainHandleSubmit as any}
                onCancel={backButtonclickHandler}
                onSourceChange={onFormSourceUpdate}
                className="wt-writing-forms-container"
                backButtonText={getFormsBackButtonText(formType, sourceType)}
              />
            </WritingFormsWrapper>
          </Styled.FormMainWrapper>
          <Advert placement="C2" />
        </LvpMarginWrapper>
      </div>
      <Advert placement="C1" />
      {!!copy && <CMSContent copy={copy} />}
    </Styled.Wrapper>
  )
}

const mapStateToProps = (state: AppState) => ({
  currentStyle: currentStyleSelector(state),
  sourceType: createCitationSourceTypeSelector(state),
  shouldBlockCitationCreation: shouldBlockOnCitationCountSelector(state),
  userRole: userRoleSelector(state),
  isProUser: isProSelector(state),
  activeCitation: activeCitationSelector(state),
  projectId: currentProjectIdSelector(state),
  searchQuery: createCitationQuerySelector(state),
  adUnlockModalExpVariant: adUnlockVariationSelector(state),
})

const mapDispatchToProps = {
  formSubmitStarted,
  writingFormSubmit,
  formLoaded,
  navigated,
  openModal,
  selectSourceType: sourceTypeSelected,
  softGateStyle,
  urlStyleNameFetched: fetchUrlStyleName,
}

export default connect<FormPageStateProps, FormPageDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(FormPage)
